import React, { useEffect, useRef, useState } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import XCircleIcon from '../icons/XCircleIcon';

type DiscountMethod = 'manual' | 'first-table';

interface DiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyDiscount: (discount: number, reason: string | undefined, method: DiscountMethod) => void;
  currentTotal: number;
  existingDiscount?: number;
  /** Optional First Table config for enabling First Table mode inside this modal. */
  firstTableConfig?: {
    enabled: boolean;
    discountPercent: number;
    eligibleSubtotal: number;
    eligibleCategoryNames: string[];
    eligibleItems: {
      name: string;
      quantity: number;
      originalTotal: number;
      discountedTotal: number;
    }[];
    orderItems: {
      name: string;
      quantity: number;
      originalTotal: number;
      isEligible: boolean;
    }[];
  };
  onApplyCoupon?: (couponCode: string) => Promise<void>;
  couponError?: string | null;
  activeCouponCode?: string | null;
  /**
   * When the order already has 1+ paid sub-bills, show a banner explaining that the discount
   * only affects the remaining (unpaid) balance — closed sub-bills are immutable.
   */
  hasPaidSubBills?: boolean;
}

const DiscountModal: React.FC<DiscountModalProps> = ({
  isOpen,
  onClose,
  onApplyDiscount,
  currentTotal,
  existingDiscount = 0,
  firstTableConfig,
  onApplyCoupon,
  couponError,
  activeCouponCode,
  hasPaidSubBills = false,
}) => {
  const hasFirstTable = !!firstTableConfig?.enabled;
  const [discountMode, setDiscountMode] = useState<DiscountMethod>('manual');
  const [discountType, setDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [discountValue, setDiscountValue] = useState('');
  const [reason, setReason] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const lastAutoAppliedScanRef = useRef<string | null>(null);
  const numericValidator = (value: string) => value === '' || /^[0-9]*\.?[0-9]*$/.test(value);
  const discountValueField = useSoftKeyboardField({
    id: 'discount-value',
    type: 'numeric',
    label:
      discountMode === 'first-table'
        ? 'First Table Discount (%)'
        : discountType === 'amount'
          ? 'Discount Amount'
          : 'Discount Percentage',
    value: discountValue,
    onChange: (value) => {
      if (numericValidator(value)) setDiscountValue(value);
    },
    manualValidator: numericValidator,
  });
  const reasonField = useSoftKeyboardField({
    id: 'discount-reason',
    type: 'alphanumeric',
    label: 'Discount Reason',
    value: reason,
    onChange: setReason,
  });

  if (!isOpen) return null;

  const baseTotalForDiscount = discountMode === 'first-table'
    ? (firstTableConfig?.eligibleSubtotal ?? 0)
    : currentTotal;

  const calculateDiscountAmount = (): number => {
    if (discountMode === 'first-table') {
      const pct = firstTableConfig?.discountPercent ?? 0;
      const base = firstTableConfig?.eligibleSubtotal ?? 0;
      if (pct <= 0 || base <= 0) return 0;
      return (base * pct) / 100;
    }

    if (!discountValue || parseFloat(discountValue) <= 0) return 0;

    if (discountType === 'percentage') {
      const percentage = parseFloat(discountValue);
      if (percentage > 100) return currentTotal; // Can't discount more than 100%
      return (currentTotal * percentage) / 100;
    } else {
      const amount = parseFloat(discountValue);
      return Math.min(amount, currentTotal); // Can't discount more than total
    }
  };

  const discountAmount = calculateDiscountAmount();
  const discountPercentage = baseTotalForDiscount > 0 ? (discountAmount / baseTotalForDiscount) * 100 : 0;
  // Manager approval threshold applies only to manual discounts, not First Table.
  const requiresApproval = discountMode === 'manual' && discountPercentage > 20;
  const newTotal = currentTotal - discountAmount;

  const handleApply = () => {
    if (discountAmount <= 0) {
      if (discountMode === 'first-table') {
        alert('No eligible items for First Table discount. Please check your categories and order items.');
      } else {
        alert('Please enter a valid discount amount');
      }
      return;
    }
    if (discountAmount > baseTotalForDiscount) {
      alert('Discount cannot exceed the eligible amount');
      return;
    }
    onApplyDiscount(discountAmount, reason.trim() || undefined, discountMode);
    handleClose();
  };

  const handleClose = () => {
    setDiscountValue('');
    setReason('');
    setDiscountType('amount');
    setCouponCode('');
    onClose();
  };

  const handleApplyCoupon = async () => {
    if (!onApplyCoupon) return;
    const normalized = normalizeCouponInput(couponCode);
    if (!normalized) {
      alert('Please enter a coupon code.');
      return;
    }
    setIsApplyingCoupon(true);
    try {
      await onApplyCoupon(normalized);
      setCouponCode('');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const normalizeCouponInput = (rawValue: string): string =>
    rawValue.trim().toUpperCase().replace(/^CPN-/, '').replace(/^UNIT-/, '');

  useEffect(() => {
    if (!onApplyCoupon) return;
    const raw = couponCode.trim().toUpperCase();
    if (!raw.startsWith('CPN-') && !raw.startsWith('UNIT-')) return;
    const extracted = normalizeCouponInput(raw);
    if (!/^[A-Z0-9]{6}$/.test(extracted)) return;
    if (lastAutoAppliedScanRef.current === raw) return;
    lastAutoAppliedScanRef.current = raw;
    setIsApplyingCoupon(true);
    void onApplyCoupon(extracted)
      .then(() => setCouponCode(''))
      .catch(() => {
        /* errors surfaced via couponError from parent */
      })
      .finally(() => setIsApplyingCoupon(false));
  }, [couponCode, onApplyCoupon]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto my-auto mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Apply Discount</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {hasPaidSubBills && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">Heads up — this order has paid sub-bills.</p>
              <p className="mt-1">
                Closed sub-bills are immutable. Applying a discount now will only reduce the remaining unpaid balance — already-paid customers will not be re-charged or refunded.
              </p>
            </div>
          )}
          {/* Discount Mode & Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Discount Type
            </label>
            {onApplyCoupon && (
              <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs font-semibold text-emerald-900 mb-2">Apply coupon code</p>
                <div className="flex gap-2">
                  <input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleApplyCoupon();
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-emerald-300 rounded-md text-sm"
                    placeholder="Enter code or scan CPN-/UNIT-XXXXXX"
                    disabled={isApplyingCoupon || !!activeCouponCode}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={isApplyingCoupon || !!activeCouponCode}
                    className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-60"
                  >
                    {isApplyingCoupon ? 'Checking...' : 'Apply'}
                  </button>
                </div>
                {activeCouponCode && (
                  <p className="text-xs text-emerald-700 mt-2">Coupon applied: {activeCouponCode}</p>
                )}
                {couponError && (
                  <p className="text-xs text-red-600 mt-2">{couponError}</p>
                )}
              </div>
            )}
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => {
                  setDiscountMode('manual');
                  setDiscountType('amount');
                  setDiscountValue('');
                }}
                className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
                  discountType === 'amount'
                    ? 'bg-pink-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Amount ($)
              </button>
              <button
                onClick={() => {
                  setDiscountMode('manual');
                  setDiscountType('percentage');
                  setDiscountValue('');
                }}
                className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
                  discountType === 'percentage'
                    ? 'bg-pink-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Percentage (%)
              </button>
              {hasFirstTable && (
                <button
                  onClick={() => {
                    setDiscountMode('first-table');
                    setDiscountType('percentage');
                    setDiscountValue(String(firstTableConfig?.discountPercent ?? 50));
                  }}
                  className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
                    discountMode === 'first-table'
                      ? 'bg-pink-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  First Table
                </button>
              )}
            </div>
          </div>

          {/* Discount Value Input */}
          {discountMode === 'first-table' ? (
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Table Percentage (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={firstTableConfig?.discountPercent ?? 50}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-700"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Set in Settings &gt; First Table Discount.
                </p>
              </div>
              {(firstTableConfig?.eligibleSubtotal ?? 0) <= 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Add items from the eligible categories to this order before applying First Table discount.
                </p>
              )}
              {firstTableConfig && (
                <div className="text-xs text-gray-600 space-y-1">
                  <p>
                    Eligible categories:{' '}
                    {firstTableConfig.eligibleCategoryNames.length > 0
                      ? firstTableConfig.eligibleCategoryNames.join(', ')
                      : 'None selected'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {discountType === 'amount' ? 'Discount Amount ($)' : 'Discount Percentage (%)'}
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  step={discountType === 'amount' ? '0.01' : '0.1'}
                  min="0"
                  max={discountType === 'percentage' ? '100' : currentTotal.toFixed(2)}
                  value={discountValue}
                  data-testid="discount-value-input"
                  {...discountValueField}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  placeholder={discountType === 'amount' ? '0.00' : '0.0'}
                  autoFocus
                />
                <span className="absolute right-3 top-2.5 text-gray-500">
                  {discountType === 'amount' ? '$' : '%'}
                </span>
              </div>
            </div>
          )}

          {/* Reason Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              {...reasonField}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              rows={2}
              placeholder="e.g., Customer complaint, Special promotion..."
            />
          </div>

          {/* Discount Summary */}
          {discountAmount > 0 && (
            <div className="bg-gray-50 p-4 rounded-md space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Current Total:</span>
                <span className="font-semibold">${currentTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount Amount:</span>
                <span className="font-semibold text-green-600">-${discountAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount Percentage:</span>
                <span className={`font-semibold ${requiresApproval ? 'text-red-600' : 'text-gray-800'}`}>
                  {discountPercentage.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t pt-2">
                <span>New Total:</span>
                <span className="text-pink-600">${newTotal.toFixed(2)}</span>
              </div>
              {discountMode === 'first-table' && firstTableConfig && (
                <div className="mt-3 border-t pt-2 space-y-2">
                  <div className="flex justify-between text-[11px] text-gray-700">
                    <span className="font-semibold">Eligible subtotal:</span>
                    <span className="ml-2 whitespace-nowrap">
                      ${firstTableConfig.eligibleSubtotal.toFixed(2)} → ${(firstTableConfig.eligibleSubtotal - discountAmount).toFixed(2)}
                    </span>
                  </div>

                  {firstTableConfig.orderItems.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Order items</p>
                      <div className="space-y-1 max-h-28 overflow-y-auto">
                        {firstTableConfig.orderItems.map((item, idx) => (
                          <div
                            key={`${item.name}-${idx}`}
                            className={`flex justify-between text-[11px] ${item.isEligible ? 'text-green-700' : 'text-gray-700'}`}
                          >
                            <span className="truncate">
                              {item.quantity}x {item.name}
                              {item.isEligible ? ' (eligible)' : ''}
                            </span>
                            <span className="ml-2 text-right whitespace-nowrap">
                              ${item.originalTotal.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {firstTableConfig.eligibleItems.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Items with First Table applied</p>
                      <div className="space-y-1 max-h-28 overflow-y-auto">
                        {firstTableConfig.eligibleItems.map((item, idx) => (
                          <div key={`${item.name}-${idx}`} className="flex justify-between text-[11px] text-green-700">
                            <span className="truncate">
                              {item.quantity}x {item.name}
                            </span>
                            <span className="ml-2 text-right whitespace-nowrap">
                              ${item.originalTotal.toFixed(2)} → ${item.discountedTotal.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {requiresApproval && (
                <div className="mt-2 p-2 bg-yellow-100 border border-yellow-400 rounded-md">
                  <p className="text-sm text-yellow-800 font-semibold">
                    ⚠️ Manager approval required (discount exceeds 20%)
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleClose}
              className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              data-testid="discount-apply-confirm"
              disabled={
                discountMode === 'first-table'
                  ? discountAmount <= 0
                  : !discountValue || parseFloat(discountValue) <= 0
              }
              className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
                requiresApproval
                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                  : 'bg-pink-500 text-white hover:bg-pink-600'
              } disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed`}
            >
              {requiresApproval ? 'Request Approval' : 'Apply Discount'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountModal;

